/* eslint-disable no-constant-condition */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { getChartColor } from "./chartColors";

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend, ChartDataLabels);

// Helper functions outside component to avoid dependency issues
const transformValueArray = (valueArray) => {
  if (!Array.isArray(valueArray)) return [];
  return valueArray.map((valor) => (valor === 0 || Number.isNaN(valor) ? null : valor));
};

const transformGroups = (groups, escolha) => {
  if (!Array.isArray(groups)) return [];

  return groups.map((item) => ({
    ...item,
    [escolha]: transformValueArray(item[escolha]),
  }));
};

const createSafeLabels = (dataLabels) => {
  return Array.isArray(dataLabels) ? [...dataLabels] : [];
};

const createSafeGroups = (dataGroups, escolha) => {
  if (!Array.isArray(dataGroups)) return [];

  return dataGroups.map((group) => ({
    ...group,
    [escolha]: Array.isArray(group[escolha]) ? [...group[escolha]] : [],
  }));
};

const processData = (inputData, escolha) => {
  // Handle invalid data early
  if (!inputData || typeof inputData !== "object") {
    return { labels: [], groups: [] };
  }

  const safeLabels = createSafeLabels(inputData.labels);
  const initialGroups = createSafeGroups(inputData.groups, escolha);

  // Transform groups if data exists
  const hasGroups = inputData?.groups?.length > 0;
  const finalGroups = hasGroups ? transformGroups(initialGroups, escolha) : initialGroups;

  return {
    labels: safeLabels,
    groups: finalGroups,
  };
};

const LineChart = ({ data, escolha }) => {
  const [modifiedData, setModifiedData] = useState(data || { labels: [], groups: [] });

  useEffect(() => {
    const processedData = processData(data, escolha);
    setModifiedData(processedData);
  }, [data, escolha]);

  // Defensive programming: ensure modifiedData is valid
  const safeModifiedData = modifiedData || { labels: [], groups: [] };
  const safeLabels = Array.isArray(safeModifiedData.labels) ? safeModifiedData.labels : [];
  const safeGroups = Array.isArray(safeModifiedData.groups) ? safeModifiedData.groups : [];

  const chartData = {
    labels: safeLabels,
    datasets: safeGroups.map((group) => {
      const groupData = Array.isArray(group[escolha]) ? group[escolha] : [];
      const color = getChartColor(group);
      return {
        label: group.name || "",
        data: groupData.map((num) => (num === 0 || num === null ? null : Math.floor((num || 0) * 100) / 100)),
        borderColor: color,
        fill: false,
        pointBackgroundColor: color,
        pointBorderColor: color,
        pointRadius: groupData.map((num) => (num === 0 || num === null ? 0 : 8)),
        pointHoverRadius: groupData.map((num) => (num === 0 || num === null ? 0 : 8)),
      };
    }),
  };

  // Calculate max value safely for y-axis
  const maxValue =
    safeGroups.length > 0
      ? Math.max(
          ...safeGroups.flatMap((group) => {
            const groupData = Array.isArray(group[escolha]) ? group[escolha] : [];
            return groupData.filter((val) => typeof val === "number" && !Number.isNaN(val));
          })
        )
      : 1;

  const options = {
    layout: {
      padding: {
        top: 0,
        bottom: 0,
        left: 40,
        right: 40,
      },
    },
    scales: {
      y: {
        grid: {
          display: true,
          drawBorder: false,
          drawTicks: false,
          lineWidth: 1,
          color: "#e0e0e0",
        },
        ticks: {
          display: false,
        },
        suggestedMin: 0,
        suggestedMax: (maxValue > 0 ? maxValue : 1) * 1.2,
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: "#01054C",
          font: {
            weight: "bold",
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        color: "#01054C",
        font: {
          weight: "bold",
          size: 12,
        },
        anchor: "end",
        align: "top",
        offset: 4,
        formatter: (value) => {
          if (value === null || value === undefined || Number.isNaN(value)) return "";
          if (value >= 1_000_000_000) return `R$ ${(value / 1_000_000_000).toFixed(1).replace(".", ",")}B`;
          if (value >= 1_000_000) return `R$ ${(value / 1_000_000).toFixed(1).replace(".", ",")}M`;
          if (value >= 1_000) return `R$ ${(value / 1_000).toFixed(1).replace(".", ",")}k`;
          return `R$ ${value.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;
        },
        clamp: true,
      },
    },
  };
  return <Line data-testid="line-chart-component" data={chartData} options={options} />;
};

LineChart.propTypes = {
  data: PropTypes.shape({
    labels: PropTypes.array,
    groups: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      })
    ),
  }),
  escolha: PropTypes.string,
};

export default LineChart;
